import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks'

/**
 * SkipLink Component
 * Based on SHARED-ACCESSIBILITY.md - Skip Links
 *
 * Allows keyboard users to skip directly to main content.
 * Visually hidden until focused.
 *
 * Usage:
 * Place at the very beginning of <body> or <App />
 */

export interface SkipLinkProps {
  /** Target element ID (without #) */
  targetId?: string
  /** Link text (uses translation if not provided) */
  children?: string
  className?: string
}

export function SkipLink({
  targetId = 'main-content',
  children,
  className,
}: SkipLinkProps) {
  const { t } = useTranslation()
  const linkText = children || t('accessibility.skipToContent')
  return (
    <a
      href={`#${targetId}`}
      className={cn(
        // Visually hidden by default
        'sr-only',
        // Visible when focused
        'focus:not-sr-only',
        'focus:absolute focus:top-4 focus:left-4 focus:z-tooltip',
        'focus:px-4 focus:py-2',
        'focus:bg-primary focus:text-white',
        'focus:rounded-md focus:font-medium',
        'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
        className
      )}
    >
      {linkText}
    </a>
  )
}

/**
 * SkipLinks - Multiple skip link targets
 */
export interface SkipLinksProps {
  links: Array<{
    targetId: string
    label: string
  }>
  className?: string
}

export function SkipLinks({ links, className }: SkipLinksProps) {
  return (
    <nav
      aria-label="Skip links"
      className={cn('sr-only focus-within:not-sr-only', className)}
    >
      <ul className="focus-within:absolute focus-within:top-4 focus-within:left-4 focus-within:z-tooltip focus-within:flex focus-within:gap-2">
        {links.map((link) => (
          <li key={link.targetId}>
            <a
              href={`#${link.targetId}`}
              className={cn(
                'sr-only focus:not-sr-only',
                'focus:px-4 focus:py-2',
                'focus:bg-primary focus:text-white',
                'focus:rounded-md focus:font-medium',
                'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2'
              )}
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
